import { useEffect, useState } from "react";
import Router from "next/router";
import Container from "../Container";
import useTranslation, { useRoutes } from "../../helpers/translationTexts/useTranslation";
import {
  RegistrationContainer,
  Title,
  SignUpButton,
  InputNickName,
  SelectedCountryInput,
  SelectCountryDropdown,
  FilterCountryInput,
  Loader,
  GoBackButton,
} from "./Registration.components";
import { finishRegistration } from "../../api/authApis";
import { useSession } from "../../context/SessionProvider";
import countries from "../../constants/countries";
import getCountryPathCode from "../../helpers/getCountryPathCode";
import { LocalStorage } from "../../utils/LocalStorage";
import syncManager from "../../api/SyncManager";
import { AuthInfoModal, SelectCountryModal } from "../modal";

const Registration: React.FC = () => {
  const { userData, setUserData } = useSession();
  const [nickname, setNickname] = useState("");
  const [filterCountry, setFilterCountry] = useState("");
  const [countryCode, setCountryCode] = useState(getCountryPathCode().code);
  const [selectedCountry, setselectedCountry] = useState(getCountryPathCode());
  const [isOpenAuthInfoModal, setIsOpenAuthInfoModal] = useState(false);
  const [isOpenCountryChangeModal, setIsOpenCountryChangeModal] = useState(false);

  const { Root: rootPath, Login: loginPath } = useRoutes();

  const authorized = userData && userData.nickname && userData.countryCode;

  const title = useTranslation("authInfoModalRegistrationTitleErrors");
  const description = useTranslation("registrationFailedMessage");
  const btnText = useTranslation("ok");
  const signUp = useTranslation("signUp");
  const goBack = useTranslation("goBack");

  useEffect(() => {
    if (!userData) {
      Router.push(loginPath);
    }
    if (authorized) {
      Router.push(rootPath);
      return;
    }
  }, [authorized, , loginPath, rootPath, userData]);
  const filteredAndSortedCountries = countries
    .filter(({ name }) => (filterCountry ? name.toLowerCase().includes(filterCountry.toLowerCase()) : true))
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleNicknameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const maxLength = 20;
    if (value.length <= maxLength) {
      setNickname(value);
    }
  };
  const handleCountryChange = (country: any) => {
    setselectedCountry(country);
    setCountryCode(country.code);
    setIsOpenCountryChangeModal(false);
    setFilterCountry("");
  };

  const doRegistration = async () => {
    if (!nickname || !/^[a-zA-Z0-9]{4,20}$/.test(nickname)) {
      return;
    }
    try {
      const updatedUserData = await finishRegistration(nickname, countryCode);
      if (typeof updatedUserData === "string") {
        setIsOpenAuthInfoModal(true);
        return;
      }
      setUserData((prev) => ({ ...prev, ...updatedUserData }));
      await syncManager.sendOfflineProgress();
      Router.push(rootPath);
    } catch (e) {
      setIsOpenAuthInfoModal(true);
    }
  };

  return (
    <Container>
      <RegistrationContainer>
        <Title>{useTranslation("createAnAccount")}</Title>
        <InputNickName nickName={nickname} handleNicknameChange={handleNicknameChange} />
        <SelectedCountryInput selectedCountry={selectedCountry} onClick={() => setIsOpenCountryChangeModal(true)} />
        {isOpenCountryChangeModal && (
          <SelectCountryModal isOpen={isOpenCountryChangeModal} onClose={() => setIsOpenCountryChangeModal(false)}>
            <FilterCountryInput value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)} />
            <SelectCountryDropdown countries={filteredAndSortedCountries} handleCountryChange={handleCountryChange} />
          </SelectCountryModal>
        )}
        {isOpenAuthInfoModal ? (
          <Loader />
        ) : (
          <>
            <SignUpButton onClick={() => doRegistration()}>{signUp}</SignUpButton>
            <GoBackButton
              onClick={() => {
                setUserData(null);
                LocalStorage.removeTokens();
              }}
            >
              {goBack}
            </GoBackButton>
          </>
        )}
      </RegistrationContainer>
      {isOpenAuthInfoModal && (
        <AuthInfoModal
          isOpen={isOpenAuthInfoModal}
          title={title}
          description={description}
          btnNameCancel={btnText}
          onCloseClicked={() => {
            setIsOpenAuthInfoModal(false);
            Router.push(rootPath);
          }}
        />
      )}
    </Container>
  );
};

export default Registration;
